import { useEffect, useState } from 'react';
import useUser from './useUser';
import { LOCAL_STORAGE_KEYS } from 'constant/localStorage';
import { USER_TYPE } from 'constant/qrcode';
import { useGetUserDataQuery } from 'api/User/queries';
import {
  useStonlyDataQuery,
  useTenantInformationQuery,
} from 'api/Common/queries';
import { getNameInitials } from 'utils/getNameInitials';
import { STATUS_CODES } from 'constant/http';
import { SESSION_STORAGE_KEYS } from 'constant/sessionStorage';
import { emitFatalLog } from 'utils/observability/logger';
import { AnyValueMap } from '@opentelemetry/api-logs';

const getHttpStatus = (error: unknown): number | undefined => {
  const e = error as
    | { response?: { status?: number }; status?: number }
    | null
    | undefined;
  return e?.response?.status ?? e?.status;
};

const isAuthRejection = (status?: number): boolean =>
  status === STATUS_CODES.Unauthorized || status === STATUS_CODES.Forbidden;

function useAuth() {
  const { user, setUser, setStonlyData, setTenantInformation } = useUser();
  // Reactive token: mirrors the tenant pattern below. A useState lazy
  // initializer captures the mount-time value and, if setToken() is ever
  // called from within this hook, re-evaluates enabled-guards and the
  // non-auth error-handler without a full remount.
  const [token] = useState(() =>
    localStorage.getItem(LOCAL_STORAGE_KEYS.TOKEN),
  );
  // Reactive tenant: localStorage is not reactive, so if the tenant is resolved
  // mid-session (tenant query writes it after mount) we must trigger a re-render
  // to re-enable the user-data query. Reading it once as a const left the query
  // permanently disabled and stranded the user on the loader until a refresh.
  const [tenant, setTenant] = useState(() =>
    localStorage.getItem(LOCAL_STORAGE_KEYS.TENANT),
  );
  const userExists = !!user && !!token;
  const isGuestUser =
    (user?.user_type === USER_TYPE.GUEST ||
      user?.user_type === USER_TYPE.NEW_GUEST) ??
    false;

  const isRegisteredVariconUser = user?.user_type === USER_TYPE.VARICON_USER;

  const [isAuthenticated, setIsAuthenticated] = useState(userExists);
  const [loading, setLoading] = useState(!userExists);

  const {
    data: tenantInformation,
    isError: isTenantInformationError,
    error: errorTenantInformation,
    isFetching: isTenantInformationFetching,
  } = useTenantInformationQuery(undefined, {
    enabled: Boolean(token),
    retry: 2,
  });

  const { data: stonlyData } = useStonlyDataQuery({
    enabled: Boolean(token) && !!tenant,
  });

  const {
    data: userData,
    isLoading: isUserDataLoading,
    isError: isUserDataError,
    error: userDataError,
    isFetching: isUserDataFetching,
  } = useGetUserDataQuery({
    enabled: Boolean(token) && !!tenant,
    retry: 2,
  });

  useEffect(() => {
    if (stonlyData) {
      setStonlyData(stonlyData);
    }
  }, [stonlyData, setStonlyData]);

  useEffect(() => {
    if (userData && !isUserDataLoading) {
      const data = {
        ...userData,
        initials: getNameInitials(userData.first_name, userData.last_name),
        id: userData.user_id as string,
      };
      localStorage.setItem(LOCAL_STORAGE_KEYS.USER_ID, data?.id);
      sessionStorage.setItem(SESSION_STORAGE_KEYS.EMAIL, data?.email);
      // Guard against absent name fields so sessionStorage never stores "undefined undefined"
      const firstName = userData.first_name ?? '';
      const lastName = userData.last_name ?? '';
      sessionStorage.setItem(
        SESSION_STORAGE_KEYS.USER_NAME,
        `${firstName} ${lastName}`.trim() || 'UnknownUser',
      );
      setUser(data);
      setIsAuthenticated(true);
      setLoading(false);
    }
  }, [userData, isUserDataLoading, setUser, setIsAuthenticated, setLoading]);

  useEffect(() => {
    if (!isUserDataLoading && !user && !token) {
      setLoading(false);
    }
  }, [isUserDataLoading, user, token]);

  useEffect(() => {
    if (!tenantInformation) return;
    setTenantInformation(tenantInformation);
    const resolvedTenantId =
      localStorage.getItem(LOCAL_STORAGE_KEYS.TENANT) ??
      tenantInformation[0]?.id;
    if (resolvedTenantId) {
      localStorage.setItem(LOCAL_STORAGE_KEYS.TENANT, resolvedTenantId);
      // Publish the tenant into reactive state so the user-data query, which is
      // gated on `!!tenant`, re-enables in the same session instead of only
      // after a manual refresh.
      setTenant((prev) =>
        prev === resolvedTenantId ? prev : resolvedTenantId,
      );
    }
    // Always refresh tenant_name in sessionStorage — it is cleared on every new
    // tab open so returning users (who already have tenant in localStorage) would
    // otherwise log 'UnknownTenant' for the entire session.
    // Nullish fallback prevents storing the literal string "undefined" when name is absent.
    // getLiveUserAttrs() falls back to 'UnknownTenant' when sessionStorage is empty.
    sessionStorage.setItem(
      SESSION_STORAGE_KEYS.TENANT_NAME,
      tenantInformation[0]?.name ?? '',
    );
  }, [tenantInformation, setTenantInformation]);

  useEffect(() => {
    // Only a genuine auth rejection (401/403) should end the session. Anything
    // else — a transient network drop, a 5xx, a request cancelled by a
    // localStorage race, or the 404 QR-screen edge case — must NOT wipe a
    // freshly-established session, otherwise a single hiccup strands the user
    // on /login. This replaces the previous "any non-404 error → clearStorage()"
    // logic that destroyed all storage on the first failed call.
    //
    // A cached error (from a previous stale-token session — query keys are
    // constant, so the error survives remounts) can coexist with an in-flight
    // refetch that carries the fresh token. Acting on the cached error would
    // stop the loader / clear storage and bounce to /login before the refetch
    // resolves, so wait until the query settles.
    if (isUserDataFetching || isTenantInformationFetching) return;

    const tenantStatus = getHttpStatus(errorTenantInformation);
    const userStatus = getHttpStatus(userDataError);

    const tenantAuthFailed =
      isTenantInformationError && isAuthRejection(tenantStatus);
    const userAuthFailed = isUserDataError && isAuthRejection(userStatus);

    if (tenantAuthFailed || userAuthFailed) {
      // Scoped clear — do not touch unrelated localStorage keys.
      localStorage.removeItem(LOCAL_STORAGE_KEYS.TOKEN);
      localStorage.removeItem(LOCAL_STORAGE_KEYS.REFRESH);
      localStorage.removeItem(LOCAL_STORAGE_KEYS.TENANT);
      localStorage.removeItem(LOCAL_STORAGE_KEYS.USER_ID);
      setUser(null);
      setIsAuthenticated(false);
      setLoading(false);

      if (userAuthFailed) {
        emitFatalLog({
          body: {
            message: 'Error fetching user data',
            data: userDataError as unknown as AnyValueMap,
          },
          attrs: {
            source: 'UseAuth',
            level: 'fatal',
            errorSrc: 'USER_DATA_ERROR',
          },
        });
      } else if (tenantAuthFailed) {
        emitFatalLog({
          body: {
            message: 'Error fetching tenant information',
            data: errorTenantInformation as unknown as AnyValueMap,
          },
          attrs: {
            source: 'UseAuth',
            errorSrc: 'TENANT_ERROR',
            level: 'fatal',
          },
        });
      }
      return;
    }

    // Non-auth failure: only preserve the loader for genuinely transient errors
    // (5xx, network drops with no HTTP status). Any 4xx that is not 401/403
    // (e.g. 400 bad-request, 498 invalid-token) still indicates the session is
    // rejected — stop loading so PrivateRoute redirects to the login page.
    // For 5xx / no-status + a valid token: keep the loader so React Query can
    // recover on the next window-focus refetch without mounting Login.tsx, which
    // would clear a still-valid token via its cleanup useEffect.

    if (isUserDataError || isTenantInformationError) {
      const activeStatus = userStatus ?? tenantStatus;

      const isTemporaryError =
        Boolean(localStorage.getItem(LOCAL_STORAGE_KEYS.TOKEN)) &&
        (activeStatus === undefined || activeStatus >= 500);

      if (!isTemporaryError) {
        setLoading(false);
      }
    }
  }, [
    isUserDataError,
    isTenantInformationError,
    isUserDataFetching,
    isTenantInformationFetching,
    setUser,
    userDataError,
    errorTenantInformation,
  ]);

  return {
    isAuthenticated,
    loading,
    setIsAuthenticated,
    isGuestUser,
    isRegisteredVariconUser,
  };
}

export default useAuth;
